// src/services/cloudinary/CloudinaryService.ts
import { CLOUDINARY_CONFIG } from './config';

interface CloudinaryResponse {
  secure_url: string;
  public_id: string;
  format: string;
  width: number;
  height: number;
  bytes: number;
  original_filename: string;
  resource_type: string;
  error?: {
    message: string;
  };
}

export interface CloudinaryUploadResult {
  url: string;
  publicId: string;
  format: string;
  size: number;
  originalFilename: string;
}

// Taille maximale par type (en octets)
const MAX_FILE_SIZES: Record<string, number> = {
  image: 10 * 1024 * 1024,  // 10MB pour les images
  document: 20 * 1024 * 1024, // 20MB pour les documents
  audio: 25 * 1024 * 1024,  // 25MB pour l'audio
  default: 15 * 1024 * 1024  // 15MB par défaut
};

// Types MIME acceptés
const ACCEPTED_MIME_TYPES: Record<string, string[]> = {
  image: ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'],
  document: ['application/pdf', 'text/csv', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'],
  audio: ['audio/webm', 'audio/mpeg', 'audio/ogg', 'audio/wav'],
};

/**
 * Détermine le resource_type Cloudinary en fonction du type MIME
 */
function getResourceType(mimeType: string): 'image' | 'raw' | 'auto' {
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/') || mimeType.startsWith('audio/')) return 'auto';
  return 'raw'; // PDF, CSV, XLSX, etc.
}

/**
 * Détermine la catégorie de fichier pour la validation de taille
 */
function getFileCategory(mimeType: string): string {
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('audio/')) return 'audio';
  return 'document';
}

export class CloudinaryService {
  /**
   * Valide un fichier avant upload (type MIME et taille)
   */
  static validateFile(file: File, allowedCategories?: string[]): { valid: boolean; error?: string } {
    const category = getFileCategory(file.type);
    
    // Vérifier la catégorie si spécifiée
    if (allowedCategories && !allowedCategories.includes(category)) {
      const allowed = allowedCategories.join(', ');
      return { valid: false, error: `Type de fichier non autorisé. Types acceptés: ${allowed}` };
    }

    // Vérifier le type MIME
    const allAccepted = Object.values(ACCEPTED_MIME_TYPES).flat();
    if (!allAccepted.includes(file.type)) {
      return { valid: false, error: `Format de fichier non supporté: ${file.type}` };
    }

    // Vérifier la taille
    const maxSize = MAX_FILE_SIZES[category] || MAX_FILE_SIZES.default;
    if (file.size > maxSize) {
      const maxMB = Math.round(maxSize / (1024 * 1024));
      return { valid: false, error: `Le fichier dépasse la taille maximale de ${maxMB}MB` };
    }

    return { valid: true };
  }

  /**
   * Upload un fichier vers Cloudinary et retourne l'URL sécurisée
   * @param file - Le fichier à uploader
   * @param folder - Le dossier Cloudinary de destination
   * @returns L'URL sécurisée (https) du fichier uploadé
   */
  static async uploadFile(file: File, folder: string): Promise<string> {
    const result = await CloudinaryService.uploadFileWithDetails(file, folder);
    return result.url;
  }

  /**
   * Upload un fichier vers Cloudinary et retourne les détails complets
   * @param file - Le fichier à uploader
   * @param folder - Le dossier Cloudinary de destination
   * @returns Détails complets de l'upload (url, publicId, format, size, etc.)
   */
  static async uploadFileWithDetails(file: File, folder: string): Promise<CloudinaryUploadResult> {
    if (!CLOUDINARY_CONFIG.cloudName || !CLOUDINARY_CONFIG.uploadPreset) {
      throw new Error('Configuration Cloudinary manquante. Vérifiez vos variables d\'environnement.');
    }

    const resourceType = getResourceType(file.type);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', CLOUDINARY_CONFIG.uploadPreset);
    formData.append('folder', folder);

    try {
      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${CLOUDINARY_CONFIG.cloudName}/${resourceType}/upload`,
        {
          method: 'POST',
          body: formData
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || `Erreur d'upload: ${response.status}`);
      }

      const data = await response.json() as CloudinaryResponse;
      
      if (data.error) {
        throw new Error(data.error.message || 'Erreur inconnue lors de l\'upload');
      }
      
      return {
        url: data.secure_url,
        publicId: data.public_id,
        format: data.format,
        size: data.bytes,
        originalFilename: data.original_filename
      };
    } catch (error) {
      console.error('Erreur lors de l\'upload vers Cloudinary:', error);
      throw error;
    }
  }

  /**
   * Upload spécifique pour les pièces jointes de journal (images + PDF)
   */
  static async uploadJournalAttachment(file: File): Promise<CloudinaryUploadResult> {
    const validation = CloudinaryService.validateFile(file, ['image', 'document']);
    if (!validation.valid) throw new Error(validation.error);
    return CloudinaryService.uploadFileWithDetails(file, 'journal_attachments');
  }

  /**
   * Upload spécifique pour les documents de déclaration (PDF uniquement)
   */
  static async uploadDeclarationDocument(file: File): Promise<CloudinaryUploadResult> {
    const validation = CloudinaryService.validateFile(file, ['document']);
    if (!validation.valid) throw new Error(validation.error);
    return CloudinaryService.uploadFileWithDetails(file, 'declaration_documents');
  }

  /**
   * Upload spécifique pour les pièces jointes du chat (tous types)
   */
  static async uploadChatAttachment(file: File): Promise<CloudinaryUploadResult> {
    const validation = CloudinaryService.validateFile(file);
    if (!validation.valid) throw new Error(validation.error);
    return CloudinaryService.uploadFileWithDetails(file, 'chat_attachments');
  }

  /**
   * Upload chat attachment avec callback de progression via le backend (proxy Cloudinary)
   * Le fichier est envoyé à notre API qui l'upload à Cloudinary côté serveur,
   * évitant ainsi les restrictions CSP sur les connexions directes à Cloudinary.
   */
  static uploadChatAttachmentWithProgress(
    file: File,
    onProgress: (percent: number) => void,
  ): { promise: Promise<CloudinaryUploadResult>; abort: () => void } {
    // Limite spécifique chat : 10 Mo (limite Cloudinary)
    const CHAT_MAX_SIZE = 10 * 1024 * 1024;
    if (file.size > CHAT_MAX_SIZE) {
      return {
        promise: Promise.reject(new Error(`Le fichier dépasse la limite de 10 Mo pour le chat`)),
        abort: () => {},
      };
    }

    const validation = CloudinaryService.validateFile(file);
    if (!validation.valid) {
      return {
        promise: Promise.reject(new Error(validation.error)),
        abort: () => {},
      };
    }

    const formData = new FormData();
    formData.append('file', file);

    const xhr = new XMLHttpRequest();

    const promise = new Promise<CloudinaryUploadResult>(async (resolve, reject) => {
      // Récupérer le token d'auth pour l'API backend
      const { getAuthToken, getCurrentCompanyId } = await import('../api/ApiService');
      const token = await getAuthToken();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const data = JSON.parse(xhr.responseText);
            // Accept response if success flag OR url field present (proxy may alter shape)
            if (data.success || data.url) {
              onProgress(100);
              resolve({
                url: data.url,
                publicId: data.publicId,
                format: data.format,
                size: data.size,
                originalFilename: data.originalFilename,
              });
            } else {
              console.error('[ChatUpload] 2xx but no success/url in response:', xhr.responseText.substring(0, 500));
              reject(new Error(data.message || "Erreur d'upload"));
            }
          } catch {
            console.error('[ChatUpload] Failed to parse response:', xhr.responseText?.substring(0, 500));
            reject(new Error("Réponse invalide du serveur"));
          }
        } else {
          console.error('[ChatUpload] Non-2xx status:', xhr.status, xhr.responseText?.substring(0, 500));
          reject(new Error(`Erreur d'upload: ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => reject(new Error('Erreur réseau')));
      xhr.addEventListener('abort', () => reject(new Error('Upload annulé')));

      // Upload vers notre backend qui forward à Cloudinary
      const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000/accounting/api/v1';
      xhr.open('POST', `${baseUrl}/chat/upload`);
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }
      const companyId = getCurrentCompanyId();
      if (companyId) {
        xhr.setRequestHeader('X-Company-Id', companyId);
      }
      xhr.setRequestHeader('X-Accounting-Client', 'Wanzo-Accounting-UI/1.0.0');
      xhr.send(formData);
    });

    return { promise, abort: () => xhr.abort() };
  }

  /**
   * Upload spécifique pour les images de profil / avatars
   */
  static async uploadAvatar(file: File): Promise<string> {
    const validation = CloudinaryService.validateFile(file, ['image']);
    if (!validation.valid) throw new Error(validation.error);
    const result = await CloudinaryService.uploadFileWithDetails(file, 'avatars');
    return result.url;
  }

  /**
   * Upload spécifique pour le logo de l'organisation
   */
  static async uploadOrganizationLogo(file: File): Promise<string> {
    const validation = CloudinaryService.validateFile(file, ['image']);
    if (!validation.valid) throw new Error(validation.error);
    const result = await CloudinaryService.uploadFileWithDetails(file, 'organization_logos');
    return result.url;
  }
}
